<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportHeader from './components/ReportHeader.vue';
import ReportDownloadButton from './components/ReportDownloadButton.vue';
import OverviewReportFilters from './components/OverviewReportFilters.vue';
import BarChart from 'shared/components/charts/ChartJsBarChart.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportsAPI from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import { formatLocalizedMonth } from 'helpers/DateHelper';
import { formatTime } from 'helpers/formatTime';
import { downloadReportRows } from './helpers/csvExportHelper';
import { generateFileName } from 'dashboard/helper/downloadHelper';

const { t, locale } = useI18n();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const channel = ref('both');
const isLoading = ref(false);
const data = ref({});

// Each chart maps a series returned by the general_status endpoint to a localized title.
const CHARTS = [
  { key: 'agentResolved', title: 'GENERAL_STATUS.CHARTS.AGENT_RESOLVED' },
  { key: 'resolvedTotal', title: 'GENERAL_STATUS.CHARTS.RESOLVED_TOTAL' },
  { key: 'conversations', title: 'GENERAL_STATUS.CHARTS.CONVERSATIONS' },
  {
    key: 'firstResponseAfterAssignment',
    title: 'GENERAL_STATUS.CHARTS.FIRST_RESPONSE',
    isTime: true,
  },
  {
    key: 'resolutionTime',
    title: 'GENERAL_STATUS.CHARTS.RESOLUTION_TIME',
    isTime: true,
  },
  {
    key: 'timeToAssignment',
    title: 'GENERAL_STATUS.CHARTS.TIME_TO_ASSIGNMENT',
    isTime: true,
  },
  { key: 'botHandoff', title: 'GENERAL_STATUS.CHARTS.BOT_HANDOFF' },
  { key: 'botResolved', title: 'GENERAL_STATUS.CHARTS.BOT_RESOLVED' },
  { key: 'enteredBot', title: 'GENERAL_STATUS.CHARTS.ENTERED_BOT' },
];

const groupByPeriod = computed(() => {
  const days = (to.value - from.value) / 86400;
  if (days > 180) return 'month';
  if (days > 31) return 'week';
  return 'day';
});

const seriesFor = key => data.value[key] || [];

const labelsFor = key =>
  seriesFor(key).map(point => {
    const date = fromUnixTime(point.timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    return `${day} ${formatLocalizedMonth(date, locale.value)}`;
  });

const buildCollection = key => ({
  labels: labelsFor(key),
  datasets: [
    {
      type: 'bar',
      backgroundColor: '#c2b5f5',
      label: '',
      data: seriesFor(key).map(point => point.value),
    },
  ],
});

const timeChartOptions = {
  scales: {
    y: {
      ticks: { callback: value => formatTime(value) },
    },
  },
  plugins: {
    tooltip: {
      callbacks: { label: ({ raw }) => formatTime(raw || 0) },
    },
  },
};

const fetchData = async () => {
  if (!from.value || !to.value) return;
  isLoading.value = true;
  try {
    const response = await ReportsAPI.getGeneralStatus({
      from: from.value,
      to: to.value,
      groupBy: groupByPeriod.value,
      businessHours: businessHours.value,
      channel: channel.value,
    });
    data.value = camelcaseKeys(response.data, { deep: true });
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = filter => {
  from.value = filter.from;
  to.value = filter.to;
  businessHours.value = filter.businessHours;
  channel.value = filter.channel ?? 'both';
  fetchData();
};

const onDownload = fileFormat => {
  const allTimestamps = [
    ...new Set(
      CHARTS.flatMap(chart =>
        seriesFor(chart.key).map(point => point.timestamp)
      )
    ),
  ].sort((a, b) => a - b);
  const headers = [
    t('GENERAL_STATUS.PERIOD'),
    ...CHARTS.map(c =>
      c.isTime
        ? t('GENERAL_STATUS.SECONDS_HEADER', { title: t(c.title) })
        : t(c.title)
    ),
  ];
  const rows = allTimestamps.map(timestamp => [
    format(fromUnixTime(timestamp), 'yyyy-MM-dd'),
    ...CHARTS.map(chart => {
      const point = seriesFor(chart.key).find(p => p.timestamp === timestamp);
      return point ? point.value : 0;
    }),
  ]);
  const fileName = generateFileName({
    type: 'general_status',
    to: to.value,
    businessHours: businessHours.value,
    fileFormat,
  });
  downloadReportRows(fileName, fileFormat, [headers, ...rows]);
};
</script>

<template>
  <ReportHeader :header-title="$t('GENERAL_STATUS.HEADER')">
    <ReportDownloadButton @download="onDownload" />
  </ReportHeader>
  <div class="flex flex-col">
    <OverviewReportFilters
      :disabled="isLoading"
      show-channel-filter
      @filter-change="onFilterChange"
    />
    <div class="relative mt-4">
      <div
        class="grid grid-cols-1 gap-4 px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 md:grid-cols-2"
      >
        <div v-for="chart in CHARTS" :key="chart.key" class="p-4 rounded-md">
          <p class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t(chart.title) }}
          </p>
          <div class="flex items-center justify-center h-64">
            <BarChart
              v-if="seriesFor(chart.key).length"
              :collection="buildCollection(chart.key)"
              :chart-options="chart.isTime ? timeChartOptions : {}"
            />
            <span v-else class="text-sm text-n-slate-10">
              {{ $t('REPORT.NO_ENOUGH_DATA') }}
            </span>
          </div>
        </div>
      </div>
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        leave-active-class="transition-opacity duration-200 ease-in"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 flex justify-center pt-40 bg-n-solid-1/70 rounded-xl pointer-events-none"
        >
          <Spinner :size="32" class="text-n-brand" />
        </div>
      </Transition>
    </div>
  </div>
</template>
